import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { Button } from '@/components/ui/Button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ChartLineIcon, ChevronDownIcon, CloseIcon, MapIcon, MenuIcon } from '@/components/ui/icons';
import { APP_BUILT_AT, APP_COMMIT, APP_VERSION, ELK_URL } from '@/config/env';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { cx } from '@/lib/cx';
import {
  displayName,
  ROLES_ADMIN_ONLY,
  ROLES_INTERNATIONAL_ARRIVALS,
  ROLES_ONLINE_TABLE,
} from '@/types/user';
import { SCHEDULE_PATH } from './routes';
import styles from './AppShell.module.css';

/**
 * The frame every signed-in screen sits in: brand, navigation, the two display
 * settings, who is signed in, and the way out.
 *
 * Navigation is filtered by role — not as security, which the server enforces on
 * every request, but because offering a link that can only answer 403 wastes the
 * reader's time and makes the app look broken.
 *
 * An administrator's five dictionary and tooling screens sit behind one trigger in
 * the bar. Seven top-level links is a bar nobody reads, and those five are not
 * where the work happens — they are where it is set up, visited once a week. The
 * ones that ARE the work stay in the open.
 *
 * Below the collapse width, everything but the brand folds into one panel behind
 * a burger — the links plus the tools simply do not fit a narrow row, and a header
 * that wraps to three ragged lines is worse than a menu. In the panel the group is
 * flattened back out: a dropdown inside a dropdown is a worse answer than a list,
 * and the panel has the room the bar does not. Both renderings read the SAME
 * `ADMIN_LINKS`, and the tools are likewise built ONCE — two lists that must be
 * kept in agreement by hand is how a link goes missing from one of them.
 */
/**
 * What the version's tooltip says — the commit, and when it was built.
 *
 * Either half can be missing: a build made without git available has no commit, and
 * a toolchain that did not substitute the stamps has neither. `undefined` rather
 * than an empty string, so nothing renders an empty tooltip.
 */
const buildDetail = (
  t: (key: Key, values?: Record<string, string | number>) => string,
  formatDateTime: (value: string) => string,
): string | undefined => {
  const parts = [
    APP_COMMIT,
    APP_BUILT_AT === '' ? '' : t('app.built', { date: formatDateTime(APP_BUILT_AT) }),
  ].filter(Boolean);

  return parts.length === 0 ? undefined : parts.join(' · ');
};

/**
 * The administrator's screens: one trigger in the bar, a flat list in the
 * collapsed panel. One array, because those are two renderings of one menu.
 */
const ADMIN_LINKS: readonly { to: string; label: Key }[] = [
  { to: '/airports', label: 'nav.airports' },
  { to: '/airlines', label: 'nav.airlines' },
  { to: '/monitor', label: 'nav.integrationMonitor' },
  { to: '/users', label: 'nav.userManagement' },
  { to: '/styleguide', label: 'nav.styleGuide' },
];

export const AppShell = () => {
  const { user, signOut, can, elkToken } = useAuth();
  const { t, formatDateTime } = useTranslation();
  const location = useLocation();

  /**
   * The path the panel was opened ON, rather than a boolean. The panel is open
   * only while the location still matches, so navigating IS closing — derived,
   * with no effect writing state and no onClick to remember on every link. Only
   * the ELK link needs a hand: it opens a new tab, so nothing here changes.
   */
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const menuOpen = openedAt === location.pathname;
  const closeMenu = () => setOpenedAt(null);

  /** The group in the bar, opened and closed on the same terms as the panel. */
  const [adminOpenedAt, setAdminOpenedAt] = useState<string | null>(null);
  const adminOpen = adminOpenedAt === location.pathname;

  /* The trigger stands in for whichever of the five is open, so it has to carry
     the active mark itself — otherwise a reader on Airports sees nothing lit. */
  const adminActive = ADMIN_LINKS.some(
    ({ to }) => location.pathname === to || location.pathname.startsWith(`${to}/`),
  );

  const headerRef = useRef<HTMLElement>(null);
  const adminRef = useRef<HTMLDivElement>(null);
  const adminTriggerRef = useRef<HTMLButtonElement>(null);
  const firstAdminLinkRef = useRef<HTMLAnchorElement>(null);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cx(styles.navLink, isActive && styles.navLinkActive);

  /**
   * The Kibana deep link, for every role. `VITE_ELK_URL` ends in `...&token=`, and
   * login hands out an `elkToken` minted for exactly this — appended, the URL signs
   * the reader straight into the dashboard. Both halves are optional: a deployment
   * without Kibana sets no URL, and a login may come back without the token. Either
   * absence means there is nothing to open, so no link — not one that lands on
   * Kibana's own login screen.
   */
  const elkHref = ELK_URL && elkToken ? `${ELK_URL}${encodeURIComponent(elkToken)}` : null;

  // The app's dropdown manners, same as ExportMenu and RangePicker: a click
  // outside closes, Escape closes.
  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenedAt(null);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenedAt(null);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // The same manners for the group, plus the keyboard courtesies a menu inside the
  // bar needs and a full-width panel does not: opening moves focus to the first
  // entry, Escape hands it back to the trigger.
  useEffect(() => {
    if (!adminOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!adminRef.current?.contains(event.target as Node)) setAdminOpenedAt(null);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAdminOpenedAt(null);
        adminTriggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    firstAdminLinkRef.current?.focus();

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [adminOpen]);

  /**
   * The five, as links. `inMenu` is the bar's rendering: the entries are a menu's,
   * and the first one takes the focus when the group opens.
   */
  const adminLinks = (inMenu: boolean) =>
    ADMIN_LINKS.map(({ to, label }, index) => (
      <NavLink
        key={to}
        to={to}
        ref={inMenu && index === 0 ? firstAdminLinkRef : undefined}
        role={inMenu ? 'menuitem' : undefined}
        className={({ isActive }) => cx(linkClass({ isActive }), inMenu && styles.navMenuLink)}
      >
        {t(label)}
      </NavLink>
    ));

  /** The group itself — the bar's rendering of the five. */
  const adminGroup = (
    <div className={styles.navGroup} ref={adminRef}>
      <button
        type="button"
        ref={adminTriggerRef}
        className={cx(styles.navLink, styles.navTrigger, adminActive && styles.navLinkActive)}
        onClick={() => setAdminOpenedAt(adminOpen ? null : location.pathname)}
        aria-expanded={adminOpen}
        aria-haspopup="menu"
      >
        {t('nav.administration')}
        <ChevronDownIcon className={styles.navCaret} />
      </button>

      {adminOpen && (
        <div className={styles.navMenu} role="menu" aria-label={t('nav.administration')}>
          {adminLinks(true)}
        </div>
      )}
    </div>
  );

  /**
   * The navigation, built once — the bar and the panel differ only in how the
   * administrator's five are presented.
   */
  const links = (variant: 'bar' | 'panel') => (
    <>
      {/* The board, and for every role but ADMIN the whole of the app. */}
      <NavLink to={SCHEDULE_PATH} className={linkClass}>
        {t('nav.onlineTable')}
      </NavLink>

      {/* The board's audience, and offered on the board's condition: it reads the
          same API, so a role the board refuses would open an empty map. */}
      {can(ROLES_ONLINE_TABLE) && (
        <NavLink to="/map" className={({ isActive }) => cx(linkClass({ isActive }), styles.iconLink)}>
          <MapIcon />
          {t('nav.flightMap')}
        </NavLink>
      )}

      {/* ADMIN and AAK — the one screen whose audience is neither everybody nor
          administrators alone, so it is offered on its own condition rather than
          folded into the block below. */}
      {can(ROLES_INTERNATIONAL_ARRIVALS) && (
        <NavLink to="/international-arrivals" className={linkClass}>
          {t('nav.internationalArrivals')}
        </NavLink>
      )}

      {/* The one link that leaves the app, so it is last and marked as leaving.
          A new tab: the board stays where it was for when the reader comes back —
          which is also why the panel has to close itself here, there being no
          route change to do it. */}
      {elkHref && (
        <a
          className={cx(styles.navLink, styles.iconLink)}
          href={elkHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
        >
          <ChartLineIcon />
          {t('nav.elkDashboard')}
        </a>
      )}
      
      {/* Everything else is an administrator's: one trigger in the bar, the five
          laid out flat in the panel. The dictionaries are here rather than on a
          wider list on purpose — see the routes in App.tsx. */}
      {can(ROLES_ADMIN_ONLY) && (variant === 'bar' ? adminGroup : <>{adminLinks(false)}</>)}

    </>
  );

  /**
   * Which build is serving.
   *
   * App chrome, so it lives in the app's chrome rather than among the profile's
   * fields, where it claimed to be a property of the signed-in account. Quiet: it is
   * read after a deploy and when somebody is describing a problem, not during a
   * shift.
   *
   * Only the VERSION is on screen. The commit and the build time are on the title:
   * they are what actually answers "is the deploy I just ran the one serving", but
   * that is a question asked twice a month, and a permanent row is not where a
   * twice-a-month answer belongs. Hovering costs nothing and gives up nothing.
   */
  const buildStamp = APP_VERSION === '' ? null : (
    <span className={styles.build} title={buildDetail(t, formatDateTime)}>
      v{APP_VERSION}
    </span>
  );

  /* Who is signed in, and the way to their own account. A link rather than a
     button: it goes to a route, so it opens in a new tab, and it is the only nav
     item that is not in the nav — it belongs beside the name it is about. */
  const profileLink = (
    <NavLink
      to="/profile"
      className={({ isActive }) => cx(styles.user, isActive && styles.userActive)}
      title={t('nav.profile')}
    >
      <span className={styles.userName}>{displayName(user)}</span>
      {/* The role is worth showing: it explains why a screen is missing. */}
      <span className={styles.userRole}>{user?.role ?? t('auth.noRole')}</span>
    </NavLink>
  );

  return (
    <div className={styles.shell}>
      <header className={styles.header} ref={headerRef}>
        <NavLink to={SCHEDULE_PATH} className={cx(styles.brand)}>
          {t('app.name')}
          {/* <span className={styles.brandTag}>{t('app.tagline')}</span> */}
        </NavLink>

        {/* The wide header. CSS hides both of these below the collapse width. */}
        <nav className={styles.nav}>{links('bar')}</nav>

        <div className={styles.tools}>
          <LanguageSwitcher />
          <ThemeToggle />
          {profileLink}
          <Button size="sm" onClick={signOut}>
            {t('auth.signOut')}
          </Button>
        </div>

        {/* The narrow header: just the burger. Hidden on wide screens by CSS. */}
        <button
          type="button"
          className={styles.menuToggle}
          onClick={() => setOpenedAt(menuOpen ? null : location.pathname)}
          aria-expanded={menuOpen}
          aria-label={t(menuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
        >
          {menuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        {menuOpen && (
          <div className={styles.menu}>
            <nav className={styles.menuNav}>{links('panel')}</nav>

            {/* The account and the tools, below a rule: they are about the session,
                not about where to go. */}
            <div className={styles.menuFoot}>
              {profileLink}
              <div className={styles.menuTools}>
                <LanguageSwitcher variant="segmented" />
                <ThemeToggle />
                <Button size="sm" onClick={signOut}>
                  {t('auth.signOut')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/*
        The screen, and the footer after it — both INSIDE the scrollport, so the
        footer is the last thing in the content rather than a strip pinned above the
        viewport's edge. On a short screen it sits directly under what is there; on a
        long one it is where you arrive after scrolling.

        `.main` is a flex column for the screens that fill it: the board and the
        journal ask for `flex: 1` instead of `height: 100%`, so they take the space
        left AFTER this row rather than all of it and then overflowing by its height.
      */}
      <main className={styles.main}>
        <Outlet />
        {buildStamp && <footer className={styles.footer}>{buildStamp}</footer>}
      </main>
    </div>
  );
};
